import React, { Fragment } from 'react';
import ColorPicker from '../../../Components/ColorPicker';

const ColorSetting = ({ id, label, propKey, fallback, edit, setEdit, themeColorKey, themeColorValue }) => (
  <div className='form-item'>
    <label className='form-lbl' style={{ color: 'var(--header-color)' }}>
      {label}
    </label>
    <ColorPicker
      id={id}
      color={themeColorValue(edit.props, propKey, fallback)}
      setColor={(c) => setEdit(prev => ({
        ...prev,
        props: { ...prev.props, [themeColorKey(propKey)]: c }
      }))}
    />
  </div>
);

const GLASS_STYLE_WIDGET_TYPES = new Set(['slider', 'number', 'segment-switch', 'text', 'menu']);

const VisualStyleSetting = ({ edit, setEdit, t }) => (
  <div className='form-item'>
    <label className='form-lbl' style={{ color: 'var(--header-color)' }} htmlFor='widget-visual-style'>
      {t.settingsLabels?.visualStyle || 'Visual Style'}
    </label>
    <select
      id='widget-visual-style'
      className='add-column'
      value={edit.props.visualStyle || 'glass'}
      onChange={(event) => setEdit(prev => ({
        ...prev,
        props: { ...prev.props, visualStyle: event.target.value }
      }))}
    >
      <option value='glass'>{t.settingsLabels?.glassStyle || 'Glass'}</option>
      <option value='classic'>{t.settingsLabels?.classicStyle || 'Classic'}</option>
    </select>
  </div>
);

const ControlWidgetStyleSettings = ({ edit, setEdit, t, themeColorKey, themeColorValue }) => {
  const updatePositiveInteger = (key, rawValue, min, max) => {
    if (rawValue === '') {
      setEdit(prev => ({ ...prev, props: { ...prev.props, [key]: '' } }));
      return;
    }
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    setEdit(prev => ({
      ...prev,
      props: { ...prev.props, [key]: Math.min(max, Math.max(min, Math.round(parsed))) }
    }));
  };

  if (edit.type === 'switch') {
    return (
      <Fragment>
        <ColorSetting
          id="switch-active-color"
          label={t.settingsLabels?.activeColor || 'Active Color'}
          propKey="color"
          fallback="#C52184"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="switch-inactive-color"
          label={t.settingsLabels?.inactiveColor || 'Inactive Color'}
          propKey="inactiveColor"
          fallback="#D1D1D6"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
      </Fragment>
    );
  }

  if (edit.type === 'push-button') {
    return (
      <Fragment>
        <ColorSetting
          id="push-button-active-color"
          label={t.settingsLabels?.activeColor || 'Active Color'}
          propKey="color"
          fallback="#4FC4D3"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="push-button-inactive-color"
          label={t.settingsLabels?.inactiveColor || 'Inactive Color'}
          propKey="inactiveColor"
          fallback="rgba(79, 196, 211, 0.48)"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="push-button-text-color"
          label={t.settingsLabels?.textColor || 'Text Color'}
          propKey="textColor"
          fallback="#FFFFFF"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="push-button-inactive-text-color"
          label={t.settingsLabels?.inactiveTextColor || 'Inactive Text Color'}
          propKey="inactiveTextColor"
          fallback="#8CE7EF"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
      </Fragment>
    );
  }

  if (edit.type === 'segment-switch') {
    return (
      <Fragment>
        <VisualStyleSetting edit={edit} setEdit={setEdit} t={t} />
        <div className='form-item'>
          <label className='form-lbl' style={{ color: 'var(--header-color)' }}>
            {t.settingsLabels?.fontSize || 'Font Size'}
          </label>
          <input
            type='number'
            min='8'
            step='1'
            className='add-column'
            value={edit.props.valueFontSize ?? edit.props.fontSize ?? 16}
            onBlur={() => {
              if (edit.props.valueFontSize !== '') return;
              setEdit(prev => ({
                ...prev,
                props: { ...prev.props, valueFontSize: 16 }
              }));
            }}
            onChange={(e) => {
              const nextValue = e.target.value;
              if (nextValue === '') {
                setEdit(prev => ({
                  ...prev,
                  props: { ...prev.props, valueFontSize: '' }
                }));
                return;
              }
              const parsed = Number(nextValue);
              if (!Number.isFinite(parsed)) return;
              setEdit(prev => ({
                ...prev,
                props: { ...prev.props, valueFontSize: Math.max(8, Math.round(parsed)) }
              }));
            }}
          />
        </div>
        {[
          { key: 'paddingLeft', label: t.settingsLabels?.paddingLeft || 'Padding Left', fallback: 8, min: 0, max: 200 },
          { key: 'paddingRight', label: t.settingsLabels?.paddingRight || 'Padding Right', fallback: 8, min: 0, max: 200 },
          { key: 'paddingTop', label: t.settingsLabels?.paddingTop || 'Padding Top', fallback: 8, min: 0, max: 200 },
          { key: 'paddingBottom', label: t.settingsLabels?.paddingBottom || 'Padding Bottom', fallback: 8, min: 0, max: 200 },
        ].map((field) => (
          <div className='form-item' key={field.key}>
            <label className='form-lbl' style={{ color: 'var(--header-color)' }}>{field.label}</label>
            <input
              type='number'
              min={field.min}
              max={field.max}
              step='1'
              className='add-column'
              value={edit.props[field.key] ?? field.fallback}
              onBlur={() => {
                if (edit.props[field.key] !== '') return;
                setEdit(prev => ({ ...prev, props: { ...prev.props, [field.key]: field.fallback } }));
              }}
              onChange={(event) => updatePositiveInteger(field.key, event.target.value, field.min, field.max)}
            />
          </div>
        ))}
        <ColorSetting
          id="segment-active-bg-color"
          label={t.settingsLabels?.activeBackgroundColor || 'Active Background Color'}
          propKey="activeBackgroundColor"
          fallback={themeColorValue(edit.props, 'activeOptionColor', 'var(--accent-color)')}
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="segment-active-text-color"
          label={t.settingsLabels?.activeTextColor || 'Active Text Color'}
          propKey="activeTextColor"
          fallback="var(--switch-thumb-color, #ffffff)"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="segment-inactive-bg-color"
          label={t.settingsLabels?.inactiveBackgroundColor || 'Inactive Background Color'}
          propKey="inactiveBackgroundColor"
          fallback="var(--switch-track-unchecked, rgba(29, 41, 61, 0.5))"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
        <ColorSetting
          id="segment-inactive-text-color"
          label={t.settingsLabels?.inactiveTextColor || 'Inactive Text Color'}
          propKey="inactiveTextColor"
          fallback="var(--text-color, #ffffff)"
          edit={edit}
          setEdit={setEdit}
          themeColorKey={themeColorKey}
          themeColorValue={themeColorValue}
        />
      </Fragment>
    );
  }

  return GLASS_STYLE_WIDGET_TYPES.has(edit.type)
    ? <VisualStyleSetting edit={edit} setEdit={setEdit} t={t} />
    : null;
};

export default ControlWidgetStyleSettings;
